import { lazy } from "react"
import type { FC, LazyExoticComponent, ReactElement } from "react"

import { type Params, useParams } from "react-router-dom"
import { useMount, useUnmount } from "react-use"

import { ESpinnerSize, Spinner } from "@/3514/components"
import { ECoachingSessionsIndividualQueryKey } from "@/3514/config"
import { type TUserState, useUser } from "@/3514/hooks"
import { type TAppDispatcher, useAppDispatcher } from "@/3514/store/app"
import { EUserRole } from "@/3514/types"
import type { TSwapSessionEntityProps } from "@/entities/swap-session"

const LazySwapSessionEntity: LazyExoticComponent<FC<TSwapSessionEntityProps>> = lazy(
    (): Promise<{ default: FC<TSwapSessionEntityProps> }> =>
        import("@/entities/swap-session").then(
            ({ SwapSessionEntity: E }): { default: FC<TSwapSessionEntityProps> } => ({
                default: E
            })
        )
)

const SwapSessionContainer: FC = (): ReactElement => {
    const appDispatcher: TAppDispatcher = useAppDispatcher()

    const { sessionId }: Readonly<Params> = useParams()

    const { user }: TUserState = useUser()

    useMount((): void => {
        appDispatcher.closeSidebar()
    })

    useUnmount((): void => {
        appDispatcher.openSidebar()
    })

    return (
        <LazySwapSessionEntity
            queryKey={ECoachingSessionsIndividualQueryKey({ participantId: user.id })}
            sessionId={Number(sessionId)}
            participantId={user.id}
            userRole={EUserRole.Participant}
            loadingFallback={<Spinner size={ESpinnerSize.Medium} wrapperClassName="top-[120px] relative" />}
        />
    )
}

SwapSessionContainer.displayName = "ParticipantSwapSessionContainer"

export { SwapSessionContainer as ParticipantSwapSessionContainer }
